import * as amplitudeSdk from '@amplitude/analytics-browser';
import { init as engagementInit } from '@amplitude/engagement-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { AMPLITUDE_KEY } from '@/config/env';

// Session Replay configuration
const SESSION_REPLAY_SAMPLE_RATE = 0.5; // 50% of sessions - adjust for production traffic

// Event names must follow consistent naming convention (pattern: [Object] + [Action] + [Optional Context])
export enum AmplitudeEvent {
  // App events
  AUTH_OPENED = 'Auth Opened',
  SSO_CALLBACK_RECEIVED = 'SSO Callback Received',
  SYNC_LOGIN_REDIRECTED = 'Sync Login Redirected',

  // Auth events
  USER_LOGIN_STARTED = 'User Login Started',
  USER_LOGGED_IN = 'User Login Succeeded',
  USER_LOGGED_OUT = 'User Logged Out',
  PASSWORD_RECOVERY_STARTED = 'Password Recovery Started',
  PASSWORD_RECOVERY_SUCCEEDED = 'Password Recovery Succeeded',
  EMAIL_VALIDATION_STARTED = 'Email Validation Started',
  EMAIL_VALIDATION_SUCCEEDED = 'Email Validation Succeeded',

  // Device events
  DEVICE_REGISTERED = 'Device Registered',
  DEVICE_VERIFIED = 'Device Verified',
  DEVICE_BLOCKED = 'Device Blocked',
  DEVICE_OTP_FAILED = 'Device OTP Failed',

  // Terms events
  TERMS_ACCEPTED = 'Terms Accepted',

  // User events
  PASSWORD_CHANGED = 'Password Changed',
  USER_SESSION_BOUND_BY_EMAIL = 'User Session Bound By Email',
  USER_SESSION_BOUND_BY_SMS = 'User Session Bound By SMS',
  USER_CONTACT_SAVED_BY_PHONE = 'User Contact Saved By Phone',
  USER_CONTACT_SAVED_BY_EMAIL = 'User Contact Saved By Email',
  USER_DATA_SAVED = 'User Data Saved',

  // Onboarding events
  DEVICE_SETUP_STARTED = 'Device Setup Started',
  ONBOARDING_STARTED = 'Onboarding Started',
  ONBOARDING_REDIRECT = 'Onboarding Redirect',
  ONBOARDING_STEP_VIEWED = 'Onboarding Step Viewed',
  ONBOARDING_LEAD_DATA_SAVED = 'Onboarding Lead Data Saved',
  ONBOARDING_COMPLETED = 'Onboarding Completed',

  // Operator events
  OPERATOR_SELECTED = 'Operator Selected',
  OPERATOR_REDIRECTED = 'Operator Redirected',
  INVITE_CODE_SUBMITTED = 'Invite Code Submitted',
  INVITE_CODE_SUCCEEDED = 'Invite Code Succeeded',

  // Complete Data events
  PERSONAL_DATA_SUBMITTED = 'Personal Data Submitted',
  PERSONAL_DATA_SUCCEEDED = 'Personal Data Succeeded',
  PERSONAL_ADDRESS_SUBMITTED = 'Personal Address Submitted',
  PERSONAL_ADDRESS_SUCCEEDED = 'Personal Address Succeeded',
  COMPANY_DATA_SUBMITTED = 'Company Data Submitted',
  COMPANY_DATA_SUCCEEDED = 'Company Data Succeeded',
  COMPANY_ADDRESS_SUBMITTED = 'Company Address Submitted',
  COMPANY_ADDRESS_SUCCEEDED = 'Company Address Succeeded',

  BANNER_CLICKED = 'Banner Clicked',
  CREDIT_REQUEST_STARTED = 'Credit Request Started',
  CREDIT_REQUEST_SUBMITTED = 'Credit Request Submitted',
  CREDIT_REQUEST_SUBMITTED_BY_RESALE = 'Credit Request Submitted By Resale',
  CREDIT_REQUEST_CANCELLED = 'Credit Request Cancelled',
  CAR_CREATED = 'CAR Created',
  CAR_UPDATED = 'CAR Updated',
  CAR_DELETED = 'CAR Deleted',
  CREDIT_PROPOSAL_ACCEPTED = 'Credit Proposal Accepted',
  CREDIT_PROPOSAL_DECLINED = 'Credit Proposal Declined',
  CREDIT_DOCUMENTS_SUBMITTED = 'Credit Documents Submitted',
  DEBT_LIST_REQUEST = 'Debt List Requested',
  FARMTECH_INTEGRATION_REQUEST = 'Farmtech Integration Requested',
  CAR_EXTRACTION_STARTED = 'CAR Extraction Started',
  CAR_EXTRACTION_FINISHED = 'CAR Extraction Finished',
  CAR_SEARCH_STARTED = 'CAR Search Started',
  CAR_SEARCH_FINISHED = 'CAR Search Finished',
  CAR_SEARCH_RESULT_EDITED = 'CAR Search Result Edited',
  CAR_SEARCH_RESULTS_DISMISSED = 'CAR Search Results Dismissed',
  CAR_IMPORT_FINISHED = 'CAR Import Finished',
  LOGGED_OUT = 'User Log Out Succeeded',
}

export type AmplitudeIdentifier = {
  isTestAccount: boolean;
  userName: string;
  userTaxId: string;
  accountName: string;
  accountTaxId: string;
};

interface InitializeParams {
  deviceId: string;
  userId: string;
}

/**
 * Initializes Amplitude Analytics and the Engagement (Guides & Surveys) SDKs in the correct order.
 *
 * The initialization sequence is critical and must follow this order:
 * 1. engagementInit: Initializes the Engagement SDK. This must be called first to set up the engagement environment.
 * 2. Add the engagement plugin to Amplitude Analytics: This step ensures that Amplitude Analytics is aware of the engagement plugin before it is initialized.
 * 3. Initialize Amplitude Analytics: Amplitude Analytics is initialized with the provided deviceId and userId. This must happen after the plugin is added.
 * 4. Boot the Engagement SDK: Finally, the Engagement SDK is booted with user identification. This must occur after Amplitude Analytics is ready, so that user context is correctly propagated.
 *
 * Changing the order of these steps may result in broken analytics or engagement features.
 *
 * @param params - The initialization parameters containing deviceId and userId.
 */
const initialize = async ({ deviceId, userId }: InitializeParams) => {
  // Initialize the Guides & Surveys SDK
  engagementInit(AMPLITUDE_KEY, { serverZone: 'US' });

  // Add the engagement plugin to Amplitude Analytics
  amplitudeSdk.add(window.engagement.plugin());

  // Create and add Session Replay Plugin
  const sessionReplayTracking = sessionReplayPlugin({
    sampleRate: SESSION_REPLAY_SAMPLE_RATE,
    forceSessionTracking: true, // Enable capture of Session Start and Session End events
  });
  amplitudeSdk.add(sessionReplayTracking);

  // Initialize Amplitude Analytics
  await amplitudeSdk.init(AMPLITUDE_KEY, { deviceId, userId }).promise;

  // Boot Guides & Surveys with user identification
  await window.engagement.boot({
    user: {
      user_id: userId,
      device_id: deviceId,
      user_properties: {},
    },
  });
};

/**
 * Set the theme mode for Guides & Surveys
 * @param mode - 'auto' | 'lightMode' | 'darkMode'
 */
const setThemeMode = (mode: 'auto' | 'lightMode' | 'darkMode') => {
  window.engagement?.setThemeMode(mode);
};

/**
 * Get list of all live guides and surveys
 */
const listGuidesAndSurveys = () => {
  return window.engagement?.gs?.list() ?? [];
};

/**
 * Show a specific guide or survey by key
 * @param key - The guide/survey key
 * @param stepIndex - Optional step to start at
 */
const showGuideOrSurvey = (key: string, stepIndex?: number) => {
  window.engagement?.gs?.show(key, stepIndex);
};

/**
 * Register a custom callback for Guides & Surveys interactions
 * @param name - The callback name configured in Amplitude
 * @param callback - The function to execute
 */
const addCallback = (name: string, callback: () => void) => {
  window.engagement?.addCallback(name, callback);
};

/**
 * Configure router for SPA navigation (use with React Router)
 * @param navigateFn - The navigation function from your router
 */
const setRouter = (navigateFn: (url: string) => void) => {
  window.engagement?.setRouter(navigateFn);
};

// Session Replay controls
/**
 * Opt out of session replay recording
 * Use this for privacy compliance (e.g., EU users)
 */
const optOutSessionReplay = () => {
  amplitudeSdk.setOptOut(true);
};

/**
 * Opt back into session replay recording
 */
const optInSessionReplay = () => {
  amplitudeSdk.setOptOut(false);
};

/**
 * Identifies user properties in Amplitude for filtering and segmentation in dashboards.
 * @param params - The user identifier properties to set
 */
const identifyUsers = ({
  isTestAccount,
  userName,
  userTaxId,
  accountName,
  accountTaxId,
}: AmplitudeIdentifier) => {
  const identifier = new amplitudeSdk.Identify();
  identifier.set('isTestAccount', isTestAccount);
  identifier.set('userName', userName);
  identifier.set('userTaxId', userTaxId);
  identifier.set('accountName', accountName);
  identifier.set('accountTaxId', accountTaxId);
  amplitudeSdk.identify(identifier);
};

export const amplitude = {
  initialize,
  identifyUsers,
  logEvent: amplitudeSdk.logEvent,
  Event: AmplitudeEvent,
  // Session Replay methods
  sessionReplay: {
    optOut: optOutSessionReplay,
    optIn: optInSessionReplay,
  },
  // Guides & Surveys methods
  guidesAndSurveys: {
    setThemeMode,
    list: listGuidesAndSurveys,
    show: showGuideOrSurvey,
    addCallback,
    setRouter,
  },
};
